import React, { createContext, ReactNode, useContext, useState } from 'react';
import { LocalHotelImageState } from '../types/zitoneoServicesTypes';
import { ComplementaryHotelImageTypeState } from '../types/zitoneoServicesTypes';
import { HotelRoomState } from '../types/zitoneoServicesTypes';
import { useZitoneoContent, useZitoneoServices } from './ZitoneoContentContext';
import api from '../../../services/api';
import endpoints from '../../../utils/commonVariables/endpoints';
import { useNotification } from '../../../contexts/NotificationsContext';
import { FeedbackMessages } from '../../../utils/commonVariables/messages';
import { IMAGE_TYPE_KEY_ROOM } from '../utils/zitoneoServicesUtils';
import { adaptDDBBHotelImageToState } from '../services/adapters/zitoneoAdapters';
import { IHotelImage } from '../../../types/entities/IHotelImage';

interface ZitoneoImagesContextType {
    hotelImageTypes: ComplementaryHotelImageTypeState[];
    selectedImageType: ComplementaryHotelImageTypeState | null;
    handleSelectImageType: (
        imageType: ComplementaryHotelImageTypeState | null
    ) => void;
    selectedRoom: HotelRoomState | null;
    handleSelectRoom: (room: HotelRoomState | null) => void;
    handleOpenImagePopUp: (bool: boolean) => void;
    isImagePopUpOpen: boolean;
    images: LocalHotelImageState[];
    handleAddImages: (images: LocalHotelImageState[]) => Promise<void>;
    isUploadingImages: boolean;
    saveImagesToDDBB: () => Promise<void>;
    handleDeleteImages: (imageId?: string) => void;
    handleUpdateImage: (
        imageId: string,
        changes: Partial<
            Pick<LocalHotelImageState, 'Caption' | 'Feature' | 'Published'>
        >
    ) => void;
}

const ZitoneoImagesContext = createContext<
    ZitoneoImagesContextType | undefined
>(undefined);

export const ProviderZitoneoImages = ({
    children,
}: {
    children: ReactNode;
}) => {
    const { ComplementaryHotelImageTypes: hotelImageTypes } =
        useZitoneoServices();
    const { hotelContent, handleAddHotelImages } = useZitoneoContent();
    const { setErrorMessage, setSuccessMessage } = useNotification();
    const [isUploadingImages, setIsUploadingImages] = useState(false);
    const [isImagePopUpOpen, setIsImagePopUpOpen] = useState(false);

    const handleOpenImagePopUp = (bool: boolean) => {
        setIsImagePopUpOpen(bool);
        if (!bool) {
            setImages([]);
            setSelectedImageType(null);
            setSelectedRoom(null);
        }
    };

    const [selectedImageType, setSelectedImageType] =
        useState<ComplementaryHotelImageTypeState | null>(null);

    const handleSelectImageType = (
        imageType: ComplementaryHotelImageTypeState | null
    ) => {
        setSelectedImageType(imageType);
    };

    const [selectedRoom, setSelectedRoom] = useState<HotelRoomState | null>(
        null
    );

    const handleSelectRoom = (room: HotelRoomState | null) => {
        setSelectedRoom(room);
    };

    const [images, setImages] = useState<LocalHotelImageState[]>([]);

    const handleAddImages = async (newImages: LocalHotelImageState[]) => {
        try {
            const imagesWithPreview = await Promise.all(
                newImages.map(async image => {
                    let previewUrl = image.Url;
                    if (image.File) {
                        const file = image.File;
                        previewUrl = await new Promise<string>(
                            (resolve, reject) => {
                                const reader = new FileReader();
                                reader.onload = () => {
                                    if (typeof reader.result === 'string') {
                                        resolve(reader.result);
                                    } else {
                                        reject(
                                            new Error('Invalid image preview')
                                        );
                                    }
                                };
                                reader.onerror = () => reject(reader.error);
                                reader.onabort = () =>
                                    reject(new Error('Image preview aborted'));
                                reader.readAsDataURL(file);
                            }
                        );
                    }
                    return { ...image, Url: previewUrl };
                })
            );
            setImages(previous => [...previous, ...imagesWithPreview]);
        } catch (error) {
            console.error(error);
            setErrorMessage(FeedbackMessages.ERROR_FETCHING);
        }
    };

    const handleUpdateImage = (
        imageId: string,
        changes: Partial<
            Pick<LocalHotelImageState, 'Caption' | 'Feature' | 'Published'>
        >
    ) => {
        setImages(previous =>
            previous.map(image =>
                image.Id === imageId ? { ...image, ...changes } : image
            )
        );
    };

    const handleDeleteImages = (imageId?: string) => {
        setImages(previous =>
            imageId ? previous.filter(image => image.Id !== imageId) : []
        );
    };

    const saveImagesToDDBB = async () => {
        const files = images.filter(image => image.File);
        const isRoomCategory = selectedImageType?.Key === IMAGE_TYPE_KEY_ROOM;

        const hotelRoomId = isRoomCategory ? selectedRoom?.Id || 0 : 0;
        const canUpload =
            hotelContent.Id &&
            selectedImageType &&
            files.length > 0 &&
            (!isRoomCategory || selectedRoom?.Id);

        if (!isUploadingImages) {
            if (canUpload) {
                setIsUploadingImages(true);
                try {
                    const filesToUpload = new FormData();
                    files.forEach(image => {
                        if (image.File) {
                            filesToUpload.append('', image.File);
                        }
                    });
                    const { data } = await api.post(
                        endpoints.addImages(
                            hotelContent.Id,
                            selectedImageType?.Id || '',
                            0,
                            hotelRoomId || 0
                        ),
                        filesToUpload
                    );
                    const failedFiles = data.Result?.failed || [];
                    if (data.Success && data.Result?.files?.length) {
                        handleAddHotelImages(
                            data.Result.files.map((image: IHotelImage) => ({
                                ...adaptDDBBHotelImageToState({
                                    ...image,
                                    Published: true,
                                    IdHotelRoomType: isRoomCategory
                                        ? Number(selectedRoom?.Id)
                                        : 0,
                                }),
                                __isAdded: true,
                                __isChanged: true,
                            }))
                        );
                        setSuccessMessage(
                            FeedbackMessages.COMMON_SUCCESS_CREATE
                        );
                        if (failedFiles.length === 0) {
                            handleOpenImagePopUp(false);
                        }
                    } else {
                        setErrorMessage(
                            data.Messages?.[0]?.Title ||
                                FeedbackMessages.ERROR_FETCHING
                        );
                    }
                    if (failedFiles.length > 0) {
                        setErrorMessage(failedFiles[0]);
                    }
                } catch (error) {
                    console.error(error);
                    setErrorMessage(FeedbackMessages.ERROR_FETCHING);
                } finally {
                    setIsUploadingImages(false);
                }
            } else {
                setErrorMessage(FeedbackMessages.ERROR_FETCHING);
            }
        }
    };

    return (
        <ZitoneoImagesContext.Provider
            value={{
                hotelImageTypes,
                selectedImageType,
                handleSelectImageType,
                selectedRoom,
                handleSelectRoom,
                handleOpenImagePopUp,
                isImagePopUpOpen,
                images,
                handleAddImages,
                isUploadingImages,
                saveImagesToDDBB,
                handleUpdateImage,
                handleDeleteImages,
            }}
        >
            {children}
        </ZitoneoImagesContext.Provider>
    );
};

export const useZitoneoImagesPopUp = (): ZitoneoImagesContextType => {
    const context = useContext(ZitoneoImagesContext);
    if (context === undefined) {
        throw new Error(
            'useZitoneoImagesPopUp must be used within a ProviderZitoneoImages'
        );
    }
    return context;
};
